import React, { useState, useEffect, useContext } from 'react';
import { Download, FileText, Zap, Brain, Database } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AppContext } from '../App';
import { useFilters } from '../context/FilterContext';

import { EXPORT_OPTIONS } from './export-center/constants';
import { ExportFiltersPanel } from './export-center/ExportFiltersPanel';
import { ExportSection } from './export-center/ExportSection';
import { useExportRunner } from './export-center/useExportRunner';

const DEFAULT_FILTERS = {
  startDate: '',
  endDate: '',
  locations: [],
  departments: [],
  supervisors: [],
  jobTitles: [],
  payTypes: [],
  employmentStatus: 'Active'
};

const ExportCenter = () => {
  const { API } = useContext(AppContext);
  const { filters: globalFilters } = useFilters();

  const [exportGranularity, setExportGranularity] = useState('summary');
  const [expandedSections, setExpandedSections] = useState({
    quickReports: true,
    detailedReports: false,
    insights: false,
    rawData: false
  });

  const [exportFilters, setExportFilters] = useState({
    ...DEFAULT_FILTERS,
    startDate: globalFilters.start_date || '',
    endDate: globalFilters.end_date || ''
  });
  const [filterOptions, setFilterOptions] = useState({
    locations: [],
    departments: [],
    supervisors: [],
    jobTitles: [],
    payTypes: []
  });
  const [loadingOptions, setLoadingOptions] = useState(true);

  // Fetch filter options and select all by default
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        setLoadingOptions(true);
        const [dimResponse, payTypesResponse] = await Promise.all([
          axios.get(`${API}/overtime/dimensions`),
          axios.get(`${API}/pay-types`)
        ]);

        const locations = dimResponse.data.locations || [];
        const departments = dimResponse.data.departments || [];
        const supervisors = dimResponse.data.managers || [];
        const jobTitles = dimResponse.data.job_titles || [];
        const payTypes = (payTypesResponse.data.pay_types || []).map(pt => pt.pay_type);

        setFilterOptions({ locations, departments, supervisors, jobTitles, payTypes });
        setExportFilters(prev => ({
          ...prev,
          locations: [...locations],
          departments: [...departments],
          supervisors: [...supervisors],
          jobTitles: [...jobTitles],
          payTypes: [...payTypes]
        }));
      } catch (error) {
        console.error('Error fetching filter options:', error);
        toast.error('Failed to load filter options');
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchFilterOptions();
  }, [API]);

  // Sync date range with global filters
  useEffect(() => {
    setExportFilters(prev => ({
      ...prev,
      startDate: globalFilters.start_date || prev.startDate,
      endDate: globalFilters.end_date || prev.endDate
    }));
  }, [globalFilters]);

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleFilterChange = (filterType, value) => {
    setExportFilters(prev => ({ ...prev, [filterType]: value }));
  };

  const toggleArrayFilter = (filterType, value) => {
    setExportFilters(prev => {
      const current = prev[filterType] || [];
      const newValues = current.includes(value)
        ? current.filter(v => v !== value)
        : [...current, value];
      return { ...prev, [filterType]: newValues };
    });
  };

  const selectAll = (filterType, options) => {
    setExportFilters(prev => ({ ...prev, [filterType]: [...options] }));
  };

  const deselectAll = (filterType) => {
    setExportFilters(prev => ({ ...prev, [filterType]: [] }));
  };

  const clearFilters = () => setExportFilters(DEFAULT_FILTERS);

  const getFilterSummary = () => {
    const parts = [];
    if (exportFilters.startDate && exportFilters.endDate) {
      parts.push(`${exportFilters.startDate} to ${exportFilters.endDate}`);
    } else if (exportFilters.startDate) {
      parts.push(`From ${exportFilters.startDate}`);
    } else if (exportFilters.endDate) {
      parts.push(`Until ${exportFilters.endDate}`);
    }
    if (exportFilters.locations.length > 0) parts.push(`${exportFilters.locations.length} location(s)`);
    if (exportFilters.departments.length > 0) parts.push(`${exportFilters.departments.length} department(s)`);
    if (exportFilters.supervisors.length > 0) parts.push(`${exportFilters.supervisors.length} supervisor(s)`);
    if (exportFilters.payTypes.length > 0) parts.push(`${exportFilters.payTypes.length} pay type(s)`);
    return parts.length > 0 ? parts.join(' • ') : 'All data (no filters applied)';
  };

  const { exporting, exportProgress, handleExport } = useExportRunner({
    API,
    exportFilters,
    filterOptions,
    exportGranularity,
    getFilterSummary
  });

  const sectionProps = {
    expanded: false, // overridden per-section below
    onToggle: toggleSection,
    exportGranularity,
    setExportGranularity,
    exporting,
    exportProgress,
    onExport: handleExport
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-[#1e3a5f] mb-1 flex items-center gap-3">
          <Download size={32} />
          Export Center
        </h1>
        <p className="text-muted-foreground">Export your data in various formats with custom filters</p>
      </div>

      <ExportFiltersPanel
        exportFilters={exportFilters}
        filterOptions={filterOptions}
        loadingOptions={loadingOptions}
        onFilterChange={handleFilterChange}
        onToggleArrayFilter={toggleArrayFilter}
        onSelectAll={selectAll}
        onDeselectAll={deselectAll}
        onClearFilters={clearFilters}
        filterSummary={getFilterSummary()}
      />

      <ExportSection
        {...sectionProps}
        sectionKey="quickReports"
        title="Quick Reports"
        icon={Zap}
        options={EXPORT_OPTIONS.quickReports}
        bgColor="bg-gradient-to-r from-green-50 to-white"
        iconColor="text-[#4CAF93]"
        expanded={expandedSections.quickReports}
      />
      <ExportSection
        {...sectionProps}
        sectionKey="detailedReports"
        title="Detailed Reports"
        icon={FileText}
        options={EXPORT_OPTIONS.detailedReports}
        bgColor="bg-gradient-to-r from-blue-50 to-white"
        iconColor="text-[#2E5C8A]"
        expanded={expandedSections.detailedReports}
      />
      <ExportSection
        {...sectionProps}
        sectionKey="insights"
        title="AI Insights"
        icon={Brain}
        options={EXPORT_OPTIONS.insights}
        bgColor="bg-gradient-to-r from-purple-50 to-white"
        iconColor="text-purple-600"
        expanded={expandedSections.insights}
      />
      <ExportSection
        {...sectionProps}
        sectionKey="rawData"
        title="Raw Data Exports"
        icon={Database}
        options={EXPORT_OPTIONS.rawData}
        bgColor="bg-gradient-to-r from-slate-50 to-white"
        iconColor="text-slate-600"
        expanded={expandedSections.rawData}
        showGranularity={true}
      />
    </div>
  );
};

export default ExportCenter;
